import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Command, FolderArchive, Activity, Send, Plus, CheckCircle2, Lock, ShieldAlert,
  Banknote, UploadCloud, FileCheck2, AlertTriangle, ExternalLink, Play, X,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import PortalNav from "@/components/PortalNav";
import { ENGINES, CLEARANCE_COLORS } from "@/content/engines";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const STATUS_FLOW = ["awaiting_payment", "in_progress", "release_requested", "completed"];
const STATUS_META = {
  awaiting_payment: { label: "Awaiting Escrow", cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10" },
  in_progress: { label: "In Progress", cls: "text-nc-blue border-nc-blue/40 bg-nc-blue/10" },
  release_requested: { label: "Release Review", cls: "text-yellow-400 border-yellow-400/40 bg-yellow-400/10" },
  completed: { label: "Completed", cls: "text-nc-green border-nc-green/40 bg-nc-green/10" },
  cancelled: { label: "Cancelled", cls: "text-zinc-400 border-white/15" },
};
const COMP_CLS = { pass: "text-nc-green border-nc-green/40", flagged: "text-red-400 border-red-500/40", unverified: "text-zinc-400 border-white/15" };
const SEV_CLS = { minor: "text-nc-blue border-nc-blue/40", major: "text-nc-orange border-nc-orange/40", critical: "text-red-400 border-red-500/40" };

const fmtGB = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${(b / 1024 ** 2).toFixed(1)} MB`);

// ---------- Sensitive Asset Transfer (multipart chunked uploader) ----------
function AssetDrop({ engagement, onDone }) {
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ title: "", version: "1.0", patch_notes: "", category: ENGINES[engagement.engine]?.categories[0] || "", secure: false });
  const [progress, setProgress] = useState(null); // {sent, total, pct}
  const [uploadId, setUploadId] = useState(null);
  const [failed, setFailed] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem("nc_token");
  const base = process.env.REACT_APP_BACKEND_URL;

  const sendChunk = async (upid, index, blob, attempt = 0) => {
    const res = await fetch(`${base}/api/market/engagements/${engagement.id}/uploads/${upid}/chunk/${index}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/octet-stream" },
      body: blob,
    });
    if (!res.ok) {
      if (attempt < 2) return sendChunk(upid, index, blob, attempt + 1);
      throw new Error(`Part ${index + 1} failed after retries`);
    }
  };

  const run = async (f, resumeId = null) => {
    setFailed(false);
    try {
      let upid = resumeId;
      let chunkSize = 4 * 1024 * 1024;
      let total = Math.ceil(f.size / chunkSize);
      let done = [];
      if (upid) {
        const st = await api.get(`/market/engagements/${engagement.id}/uploads/${upid}`);
        done = st.data.chunks_received;
        total = st.data.total_chunks;
        chunkSize = st.data.chunk_size;
      } else {
        const init = await api.post(`/market/engagements/${engagement.id}/uploads/init`, { filename: f.name, size: f.size, content_type: f.type || "application/octet-stream" });
        upid = init.data.upload_id;
        total = init.data.total_chunks;
        chunkSize = init.data.chunk_size;
        setUploadId(upid);
      }
      for (let i = 0; i < total; i++) {
        if (done.includes(i)) {
          setProgress({ sent: i + 1, total, pct: Math.round(((i + 1) / total) * 100) });
          continue;
        }
        await sendChunk(upid, i, f.slice(i * chunkSize, (i + 1) * chunkSize));
        setProgress({ sent: i + 1, total, pct: Math.round(((i + 1) / total) * 100) });
      }
      await api.post(`/market/engagements/${engagement.id}/uploads/${upid}/complete`, meta);
      toast.success("Sensitive Asset Transfer complete — file secured in the vault");
      setFile(null);
      setProgress(null);
      setUploadId(null);
      onDone();
    } catch (err) {
      setFailed(true);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const ready = file && meta.title.trim().length >= 2 && meta.patch_notes.trim().length >= 5;

  return (
    <div data-testid="asset-drop" className="border border-dashed border-nc-blue/40 bg-nc-blue/5 p-4 flex flex-col gap-3">
      <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2">
        <ShieldAlert className="w-3.5 h-3.5" /> Sensitive Asset Transfer // High-Bandwidth Channel (up to 12GB, resume-on-fail)
      </p>
      {!file ? (
        <button data-testid="asset-drop-select" onClick={() => fileRef.current?.click()} className="border border-white/10 hover:border-nc-blue py-8 font-mono text-[11px] text-zinc-400 hover:text-nc-blue transition-colors flex flex-col items-center gap-2">
          <UploadCloud className="w-6 h-6" /> Drop payload or click to select
        </button>
      ) : (
        <div className="flex flex-col gap-2.5">
          <p className="font-mono text-[10px] text-zinc-300">{file.name} <span className="text-zinc-600">// {fmtGB(file.size)}</span></p>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="asset-title" className={inputCls} placeholder="Asset title *" value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} />
            <input data-testid="asset-version" className={inputCls} placeholder="Version" value={meta.version} onChange={(e) => setMeta((m) => ({ ...m, version: e.target.value }))} />
          </div>
          <select data-testid="asset-category" className={inputCls} value={meta.category} onChange={(e) => setMeta((m) => ({ ...m, category: e.target.value }))}>
            {(ENGINES[engagement.engine]?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <textarea data-testid="asset-patchnotes" rows={2} className={`${inputCls} resize-none`} placeholder="Patch notes * (mandatory — what changed?)" value={meta.patch_notes} onChange={(e) => setMeta((m) => ({ ...m, patch_notes: e.target.value }))} />
          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="asset-secure" type="checkbox" checked={meta.secure} onChange={(e) => setMeta((m) => ({ ...m, secure: e.target.checked }))} className="accent-[#FF6B1A]" />
            <span className="font-mono text-[9px] text-zinc-400 uppercase tracking-[0.15em]">Seal under Secure Handoff (client unlocks after fund release)</span>
          </label>
          {progress && (
            <div>
              <div className="h-1.5 bg-white/10"><div data-testid="asset-progress-bar" className="h-full bg-nc-blue transition-all" style={{ width: `${progress.pct}%` }} /></div>
              <p className="font-mono text-[9px] text-zinc-500 mt-1">Transmitting part {progress.sent}/{progress.total} — {progress.pct}%</p>
            </div>
          )}
          <div className="flex gap-2">
            <button data-testid="asset-upload-start" disabled={!ready || (progress && !failed)} onClick={() => run(file, failed ? uploadId : null)} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[9px] px-4 py-2.5 transition-colors">
              {failed ? "Resume Transfer" : "Begin Transfer"}
            </button>
            <button onClick={() => { setFile(null); setProgress(null); setUploadId(null); setFailed(false); }} className="font-mono text-[9px] uppercase text-zinc-500 px-2 flex items-center gap-1"><X className="w-3 h-3" /> Abort</button>
          </div>
        </div>
      )}
      <input ref={fileRef} data-testid="asset-file-input" type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setMeta((m) => ({ ...m, title: m.title || f.name })); } }} />
    </div>
  );
}

export default function CommandPage() {
  const navigate = useNavigate();
  const { fmt, currency } = useCurrency();
  const [params] = useSearchParams();
  const [me, setMe] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [eng, setEng] = useState(null);
  const [tab, setTab] = useState("overview");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [report, setReport] = useState({ title: "", description: "", severity: "minor" });
  const [validationUrl, setValidationUrl] = useState("");
  const [deliv, setDeliv] = useState({ title: "", file_url: "", version: "1.0", patch_notes: "", category: "", secure: false });
  const [earnings, setEarnings] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => { localStorage.removeItem("nc_token"); navigate("/auth"); });
  }, [navigate]);

  const loadList = useCallback(() => {
    api.get("/market/engagements").then((r) => setEngagements(r.data)).catch(() => {});
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const r = await api.get(`/market/engagements/${id}`);
      setEng(r.data);
      const c = await api.get(`/market/engagements/${id}/chat`);
      setMessages(c.data.messages);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (me) loadList(); }, [me, loadList]);
  useEffect(() => {
    if (me && ["dev", "admin"].includes(me.role)) {
      const loadE = () => api.get("/market/earnings").then((r) => setEarnings(r.data)).catch(() => {});
      loadE();
      const id = setInterval(loadE, 8000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [me]);
  useEffect(() => {
    const target = params.get("engagement");
    if (me && target) loadDetail(target);
  }, [me, params, loadDetail]);
  useEffect(() => {
    if (!eng) return undefined;
    const id = setInterval(() => loadDetail(eng.id), 5000);
    return () => clearInterval(id);
  }, [eng, loadDetail]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!me) return <div className="min-h-screen bg-[#050505]" />;

  const isClient = eng && eng.client_id === me.id;
  const isDev = eng && eng.dev_id === me.id;
  const isAdmin = me.role === "admin";
  const engine = eng ? ENGINES[eng.engine] : null;

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      loadDetail(eng.id);
      loadList();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const send = async () => {
    if (!draft.trim()) return;
    try {
      await api.post(`/market/engagements/${eng.id}/chat`, { text: draft.trim() });
      setDraft("");
      loadDetail(eng.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="command-page" className="min-h-screen bg-[#050505] text-zinc-100 pt-6 pb-16 px-4 sm:px-6">
      <div className="max-w-[1500px] mx-auto flex flex-col gap-5">
        <PortalNav title="Project Workspace" accent="text-nc-orange" />
        <div>
          <p className="font-mono text-[9px] tracking-[0.35em] text-nc-orange uppercase">Specpixel // Projects & Escrow</p>
          <h1 className="font-sans font-bold text-2xl uppercase tracking-wider mt-1 flex items-center gap-3"><Command className="w-6 h-6 text-nc-orange" /> Project Workspace</h1>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-5 items-start">
          {/* Engagement list */}
          <div className="border border-white/10 bg-black/40">
            <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Your Projects ({engagements.length})</div>
            {engagements.length === 0 && (
              <p data-testid="engagements-empty" className="font-mono text-[10px] text-zinc-600 p-4 leading-relaxed">
                No projects yet. Hire a verified specialist from the Service Gallery or purchase a listing from the Modding Marketplace.
              </p>
            )}
            {engagements.map((e, i) => (
              <div key={e.id} data-testid={`engagement-row-${i + 1}`} onClick={() => loadDetail(e.id)} className={`px-4 py-3 border-b border-white/5 cursor-pointer transition-colors ${eng?.id === e.id ? "bg-nc-orange/10 border-l-2 border-l-nc-orange" : "hover:bg-white/5"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{e.brief.slice(0, 44)}{e.brief.length > 44 ? "…" : ""}</p>
                  <span className={`font-mono text-[8px] tracking-[0.15em] uppercase border px-1.5 py-0.5 shrink-0 ${STATUS_META[e.status]?.cls}`}>{STATUS_META[e.status]?.label}</span>
                </div>
                <p className="font-mono text-[9px] text-zinc-500 mt-0.5">{ENGINES[e.engine]?.short || e.engine} // {isAdmin || e.client_id !== me.id ? `client ${e.client_name}` : `contractor ${e.dev_name}`} // {fmt(e.quote_amount)}</p>
              </div>
            ))}
          </div>

          {/* Dev earnings wallet */}
          {earnings && (
            <div data-testid="earnings-wallet" className="border border-white/10 bg-black/40 mt-5">
              <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-green uppercase flex items-center gap-2"><Banknote className="w-3.5 h-3.5" /> Earnings Wallet</div>
              <div className="p-4 flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="border border-white/10 px-2 py-2">
                    <p data-testid="earnings-available" className="font-sans font-bold text-lg text-nc-green">{fmt(earnings.available)}</p>
                    <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Available</p>
                  </div>
                  <div className="border border-white/10 px-2 py-2">
                    <p className="font-sans font-bold text-lg text-zinc-100">{fmt(earnings.earned)}</p>
                    <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Lifetime</p>
                  </div>
                </div>
                <button
                  data-testid="payout-request"
                  disabled={earnings.available < 10}
                  onClick={() => act(async () => {
                    await api.post("/market/payouts/request", { amount: earnings.available });
                    const r = await api.get("/market/earnings");
                    setEarnings(r.data);
                  }, "Withdrawal requested — admin processes via Payoneer")}
                  className="bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.15em] text-[9px] px-3 py-2.5 transition-colors"
                >
                  Withdraw via Payoneer
                </button>
                {earnings.available < 10 && <p className="font-mono text-[8px] text-zinc-600">Minimum withdrawal AUD 10.00</p>}
                {earnings.payouts.slice(0, 3).map((p, i) => (
                  <div key={p.id} data-testid={`payout-row-${i + 1}`} className="flex justify-between font-mono text-[9px] border-b border-white/5 pb-1.5">
                    <span className="text-zinc-400">{fmt(p.amount)} // {String(p.created_at).slice(0, 10)}</span>
                    <span className={`uppercase ${p.status === "paid" ? "text-nc-green" : p.status === "pending" ? "text-nc-orange" : "text-red-400"}`}>{p.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detail */}
          {eng ? (
            <div className="flex flex-col gap-5 min-w-0">
              {/* Header */}
              <div className="border border-white/10 bg-black/40 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-1 flex items-center gap-1.5 ${engine?.accent}`}>
                        {engine && <engine.icon className="w-3.5 h-3.5" />} {engine?.label}{eng.engine_version ? ` ${eng.engine_version}` : ""}
                      </span>
                      {eng.frameworks?.map((f) => <span key={f} className="font-mono text-[9px] uppercase border border-nc-orange/40 text-nc-orange px-2 py-1">{f}</span>)}
                      <span className="font-mono text-[9px] uppercase border border-white/15 text-zinc-400 px-2 py-1">{eng.type === "purchase" ? "Marketplace Acquisition" : "Direct Hire"}</span>
                    </div>
                    <p data-testid="engagement-brief" className="font-mono text-[11px] text-zinc-300 mt-3 leading-relaxed">{eng.brief}</p>
                    <p className="font-mono text-[9px] text-zinc-600 mt-2">CLIENT {eng.client_name} // CONTRACTOR {eng.dev_name} // REF SP-OPS-{eng.id.slice(0, 6).toUpperCase()}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase">Escrow</p>
                    <p data-testid="escrow-total" className="font-sans font-bold text-xl text-nc-green">{fmt(eng.total_charged)}</p>
                    <p className="font-mono text-[9px] text-zinc-600">quote {eng.quote_amount.toFixed(2)} + fee {eng.service_fee_amount.toFixed(2)}</p>
                  </div>
                </div>
                {/* Status pipeline */}
                <div className="flex gap-1 mt-4">
                  {STATUS_FLOW.map((s) => {
                    const current = STATUS_FLOW.indexOf(eng.status) >= STATUS_FLOW.indexOf(s);
                    return <span key={s} className={`flex-1 h-1 ${current ? "bg-nc-orange" : "bg-white/10"}`} title={STATUS_META[s].label} />;
                  })}
                </div>
                <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase mt-1.5">{STATUS_META[eng.status]?.label}</p>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 flex-wrap">
                {[["overview", "Escrow & Validation"], ["vault", "Asset Vault"], ["reports", `Discrepancy Reports (${eng.reports?.length || 0})`], ["activity", "Audit Trail"], ["chat", "Comms"]].map(([t, label]) => (
                  <button key={t} data-testid={`cmd-tab-${t}`} onClick={() => setTab(t)} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${tab === t ? "text-nc-orange border-nc-orange/60 bg-nc-orange/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
                    {label}
                  </button>
                ))}
              </div>

              {tab === "overview" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
                  {/* Escrow panel */}
                  <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-2.5">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-nc-green uppercase flex items-center gap-2"><Banknote className="w-3.5 h-3.5" /> Escrow Ledger</p>
                    {[["Contractor quote", eng.quote_amount], [`Platform service fee (${eng.service_fee_pct}%)`, eng.service_fee_amount], ["Total charged to client", eng.total_charged]].map(([l, v]) => (
                      <div key={l} className="flex justify-between font-mono text-[11px] border-b border-white/5 pb-1.5"><span className="text-zinc-400">{l}</span><span className="text-zinc-100">{fmt(v)}</span></div>
                    ))}
                    <div className="flex justify-between font-mono text-[11px] border-b border-white/5 pb-1.5"><span className="text-zinc-400">Platform fee on release ({eng.platform_fee_pct}%)</span><span className="text-nc-orange">{fmt(eng.platform_fee_amount)}</span></div>
                    <div className="flex justify-between font-mono text-[11px]"><span className="text-zinc-400">Contractor payout on completion</span><span className="text-nc-green font-bold">{fmt(eng.quote_amount - eng.platform_fee_amount)}</span></div>
                    {eng.status === "awaiting_payment" && isClient && (
                      <button
                        data-testid="escrow-pay"
                        onClick={() => act(async () => {
                          const r = await api.post(`/market/engagements/${eng.id}/checkout`, { origin_url: window.location.origin, currency });
                          window.location.href = r.data.checkout_url;
                        })}
                        className="mt-2 bg-nc-green hover:bg-nc-green/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-3 transition-colors"
                      >
                        Fund Escrow via Stripe — AUD {eng.total_charged.toFixed(2)}
                      </button>
                    )}
                    {eng.status === "in_progress" && isAdmin && (
                      <button
                        data-testid="escrow-refund"
                        onClick={() => { if (window.confirm(`Cancel this engagement and refund AUD ${eng.total_charged.toFixed(2)} to the client's card?`)) act(() => api.post(`/market/engagements/${eng.id}/refund`), "Escrow refunded to client"); }}
                        className="mt-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 font-mono uppercase tracking-[0.2em] text-[10px] px-4 py-3 transition-colors"
                      >
                        Cancel & Refund Escrow
                      </button>
                    )}
                    {eng.status === "release_requested" && isAdmin && (
                      <div className="flex gap-2 mt-2">
                        <button data-testid="escrow-release" onClick={() => act(() => api.post(`/market/engagements/${eng.id}/release`), "Funds released — handoff unlocked")} className="bg-nc-green hover:bg-nc-green/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-3 transition-colors">
                          Approve & Release Funds
                        </button>
                        <button data-testid="escrow-decline" onClick={() => act(() => api.post(`/market/engagements/${eng.id}/decline-release`, { reason: "Work requires revision" }), "Release declined")} className="border border-red-400/40 text-red-400 hover:bg-red-400/10 font-mono uppercase tracking-[0.2em] text-[10px] px-4 py-3 transition-colors">
                          Decline
                        </button>
                      </div>
                    )}
                    {eng.status === "in_progress" && isDev && (
                      <button
                        data-testid="request-release"
                        disabled={!eng.final_validation?.url}
                        onClick={() => act(() => api.post(`/market/engagements/${eng.id}/request-release`), "Release requested — admin notified")}
                        className="mt-2 bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-3 transition-colors"
                      >
                        Request Fund Release
                      </button>
                    )}
                    {eng.status === "in_progress" && isDev && !eng.final_validation?.url && (
                      <p className="font-mono text-[9px] text-yellow-400">Final Validation evidence required before release can be requested.</p>
                    )}
                  </div>

                  {/* Final Validation */}
                  <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><FileCheck2 className="w-3.5 h-3.5" /> Test Environment & Proof</p>
                    {eng.final_validation?.url ? (
                      <div data-testid="validation-proof" className="border border-nc-green/30 bg-nc-green/5 p-3">
                        <p className="font-mono text-[9px] tracking-[0.2em] text-nc-green uppercase mb-1.5">Final Validation on record</p>
                        <a href={eng.final_validation.url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-nc-blue hover:text-zinc-100 break-all flex items-center gap-1.5 transition-colors"><Play className="w-3.5 h-3.5 shrink-0" /> {eng.final_validation.url}</a>
                        <p className="font-mono text-[9px] text-zinc-500 mt-1.5">{eng.final_validation.note} // verified by client before release</p>
                      </div>
                    ) : (
                      <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
                        Before funds can be released, the contractor must upload proof — a video walkthrough or test-server link — showing the work running in {engine?.label}.
                      </p>
                    )}
                    {isDev && eng.status === "in_progress" && (
                      <div className="flex gap-2">
                        <input data-testid="validation-url" className={inputCls} placeholder="https://youtu.be/… or fivem://connect/…" value={validationUrl} onChange={(e) => setValidationUrl(e.target.value)} />
                        <button data-testid="validation-submit" disabled={validationUrl.trim().length < 5} onClick={() => act(() => api.post(`/market/engagements/${eng.id}/validation`, { url: validationUrl, note: "Target-engine validation" }).then(() => setValidationUrl("")), "Validation recorded")} className="shrink-0 px-3 bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-mono text-[9px] uppercase tracking-[0.15em] transition-colors">
                          Upload
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tab === "vault" && (
                <div className="flex flex-col gap-4">
                  {(isDev || isAdmin) && ["in_progress", "release_requested"].includes(eng.status) && (
                    <>
                      <AssetDrop engagement={eng} onDone={() => loadDetail(eng.id)} />
                      {/* URL-based drop */}
                      <div className="border border-white/10 bg-black/40 p-4 flex flex-col gap-2">
                        <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-400 uppercase flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Link-Based Drop (Drive / CDN)</p>
                        <div className="grid grid-cols-2 gap-2">
                          <input data-testid="deliv-title" className={inputCls} placeholder="Asset title *" value={deliv.title} onChange={(e) => setDeliv((d) => ({ ...d, title: e.target.value }))} />
                          <input data-testid="deliv-url" className={inputCls} placeholder="https://… *" value={deliv.file_url} onChange={(e) => setDeliv((d) => ({ ...d, file_url: e.target.value }))} />
                          <input data-testid="deliv-version" className={inputCls} placeholder="Version" value={deliv.version} onChange={(e) => setDeliv((d) => ({ ...d, version: e.target.value }))} />
                          <select data-testid="deliv-category" className={inputCls} value={deliv.category || engine?.categories[0]} onChange={(e) => setDeliv((d) => ({ ...d, category: e.target.value }))}>
                            {(engine?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <textarea data-testid="deliv-patchnotes" rows={2} className={`${inputCls} resize-none`} placeholder="Patch notes * (mandatory)" value={deliv.patch_notes} onChange={(e) => setDeliv((d) => ({ ...d, patch_notes: e.target.value }))} />
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input data-testid="deliv-secure" type="checkbox" checked={deliv.secure} onChange={(e) => setDeliv((d) => ({ ...d, secure: e.target.checked }))} className="accent-[#FF6B1A]" />
                          <span className="font-mono text-[9px] text-zinc-400 uppercase tracking-[0.15em]">Seal under Secure Handoff</span>
                        </label>
                        <button data-testid="deliv-submit" disabled={deliv.title.trim().length < 2 || deliv.file_url.trim().length < 5 || deliv.patch_notes.trim().length < 5} onClick={() => act(() => api.post(`/market/engagements/${eng.id}/deliverables`, { ...deliv, category: deliv.category || engine?.categories[0] }).then(() => setDeliv({ title: "", file_url: "", version: "1.0", patch_notes: "", category: "", secure: false })), "Deliverable added")} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[9px] px-3 py-2 transition-colors">
                          Add to Vault
                        </button>
                      </div>
                    </>
                  )}
                  {eng.deliverables.length === 0 && <p data-testid="vault-empty" className="font-mono text-[10px] text-zinc-600">Vault is empty — contractor payload will land here.</p>}
                  {eng.deliverables.map((d, i) => (
                    <div key={d.id} data-testid={`vault-item-${i + 1}`} className="border border-white/10 bg-black/40 p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="font-sans font-semibold text-sm text-zinc-100">{d.title} <span className="text-zinc-500 font-mono text-[10px]">v{d.version}</span></p>
                          <p className="font-mono text-[9px] text-zinc-500 mt-0.5">{d.category} // {d.uploaded_by} // {String(d.created_at).slice(0, 10)}{d.size_bytes ? ` // ${fmtGB(d.size_bytes)}` : ""}{d.transfer === "multipart" ? " // MULTIPART" : ""}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`font-mono text-[8px] tracking-[0.15em] uppercase border px-2 py-0.5 ${COMP_CLS[d.compliance]}`}>{d.compliance === "pass" ? "Compliance OK" : d.compliance === "flagged" ? "Non-Compliant" : "Unverified"}</span>
                          {d.sealed || (d.secure && eng.status !== "completed" && isClient) ? (
                            <span data-testid={`vault-sealed-${i + 1}`} className="font-mono text-[8px] tracking-[0.15em] uppercase border border-yellow-400/40 text-yellow-400 px-2 py-0.5 flex items-center gap-1"><Lock className="w-3 h-3" /> Sealed</span>
                          ) : d.file_url && (
                            <a data-testid={`vault-download-${i + 1}`} href={d.file_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${d.file_url}` : d.file_url} target="_blank" rel="noopener noreferrer" className="text-nc-blue hover:text-zinc-100 transition-colors"><ExternalLink className="w-4 h-4" /></a>
                          )}
                        </div>
                      </div>
                      <p className="font-mono text-[10px] text-zinc-400 mt-2 leading-relaxed border-l-2 border-nc-blue/40 pl-3">{d.patch_notes}</p>
                    </div>
                  ))}
                </div>
              )}

              {tab === "reports" && (
                <div className="flex flex-col gap-4">
                  <div className="border border-white/10 bg-black/40 p-4 flex flex-col gap-2">
                    <p className="font-mono text-[9px] tracking-[0.3em] text-red-400 uppercase flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> File Discrepancy Report</p>
                    <div className="grid grid-cols-[1fr_140px] gap-2">
                      <input data-testid="report-title" className={inputCls} placeholder="Issue title * (min 5 chars)" value={report.title} onChange={(e) => setReport((r) => ({ ...r, title: e.target.value }))} />
                      <select data-testid="report-severity" className={inputCls} value={report.severity} onChange={(e) => setReport((r) => ({ ...r, severity: e.target.value }))}>
                        <option value="minor">Minor</option><option value="major">Major</option><option value="critical">Critical</option>
                      </select>
                    </div>
                    <textarea data-testid="report-description" rows={2} className={`${inputCls} resize-none`} placeholder="Reproduction steps, expected vs actual… *" value={report.description} onChange={(e) => setReport((r) => ({ ...r, description: e.target.value }))} />
                    <button data-testid="report-submit" disabled={report.title.trim().length < 5 || report.description.trim().length < 10} onClick={() => act(() => api.post(`/market/engagements/${eng.id}/reports`, report).then(() => setReport({ title: "", description: "", severity: "minor" })), "Discrepancy filed")} className="self-start bg-red-500/80 hover:bg-red-500 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[9px] px-3 py-2 transition-colors">
                      File Report
                    </button>
                  </div>
                  {(eng.reports || []).slice().reverse().map((r, i) => (
                    <div key={r.id} data-testid={`report-${i + 1}`} className="border border-white/10 bg-black/40 p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p className="font-sans font-semibold text-sm text-zinc-100">{r.title}</p>
                        <div className="flex gap-2">
                          <span className={`font-mono text-[8px] uppercase border px-2 py-0.5 ${SEV_CLS[r.severity]}`}>{r.severity}</span>
                          <span className={`font-mono text-[8px] uppercase border px-2 py-0.5 ${r.status === "resolved" ? "text-nc-green border-nc-green/40" : r.status === "acknowledged" ? "text-nc-orange border-nc-orange/40" : "text-zinc-400 border-white/15"}`}>{r.status}</span>
                        </div>
                      </div>
                      <p className="font-mono text-[10px] text-zinc-400 mt-1.5 leading-relaxed">{r.description}</p>
                      <p className="font-mono text-[9px] text-zinc-600 mt-1">{r.reported_by} // {String(r.created_at).slice(0, 10)}</p>
                      {(isDev || isAdmin) && r.status !== "resolved" && (
                        <div className="flex gap-2 mt-2">
                          {r.status === "open" && <button data-testid={`report-ack-${i + 1}`} onClick={() => act(() => api.patch(`/market/engagements/${eng.id}/reports/${r.id}`, { status: "acknowledged" }))} className="font-mono text-[8px] uppercase border border-nc-orange/40 text-nc-orange px-2 py-1 hover:bg-nc-orange/10 transition-colors">Acknowledge</button>}
                          <button data-testid={`report-resolve-${i + 1}`} onClick={() => act(() => api.patch(`/market/engagements/${eng.id}/reports/${r.id}`, { status: "resolved" }))} className="font-mono text-[8px] uppercase border border-nc-green/40 text-nc-green px-2 py-1 hover:bg-nc-green/10 transition-colors">Resolve</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {tab === "activity" && (
                <div className="border border-white/10 bg-black/40 p-5">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase mb-4 flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> Operations Audit Trail</p>
                  <div className="flex flex-col">
                    {(eng.activity || []).slice().reverse().map((a, i) => (
                      <div key={a.id} data-testid={`activity-${i + 1}`} className="flex gap-3 border-b border-white/5 py-2.5 font-mono text-[10px]">
                        <span className="text-zinc-600 shrink-0 w-[130px]">{String(a.at).slice(5, 19).replace("T", " ")}</span>
                        <span className="text-nc-orange shrink-0 uppercase w-[180px] truncate">{a.action}</span>
                        <span className="text-zinc-400 truncate">{a.actor}</span>
                        <span className="text-zinc-500 truncate">{a.detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === "chat" && (
                <div className="border border-white/10 bg-black/40 flex flex-col">
                  <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">Project Chat</div>
                  <div data-testid="cmd-chat-messages" className="overflow-y-auto p-4 flex flex-col gap-2.5 max-h-[380px] min-h-[200px]">
                    {messages.map((m) => {
                      const mine = m.sender_id === me.id;
                      return (
                        <div key={m.id} className={`max-w-[80%] px-3 py-2 border ${mine ? "self-end bg-nc-blue/10 border-nc-blue/30" : "self-start bg-white/5 border-white/10"}`}>
                          <p className={`font-mono text-[8px] tracking-[0.2em] uppercase mb-1 ${mine ? "text-nc-blue" : "text-nc-orange"}`}>{m.sender_name}</p>
                          <p className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                  <div className="border-t border-white/10 p-3 flex gap-2">
                    <input data-testid="cmd-chat-input" className={inputCls} placeholder="Transmit…" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
                    <button data-testid="cmd-chat-send" onClick={send} disabled={!draft.trim()} className="shrink-0 w-11 bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white flex items-center justify-center transition-colors"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-white/10 bg-black/40 p-10 text-center">
              <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">Select a project to open its workspace</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
